if ( typeof VideoComponent !== 'function' ) {

  class VideoComponent extends HTMLElement {

    constructor() {

      super();

      this._prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._autoplayArmed = false;

      // Set the IntersectionObserver
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Element in viewport
            this.onEnterViewport();
          } else {
            // Element out of viewport
            this.onExitViewport();
          }
        });
      }, {
        threshold: [0, 1]
      });

      // Observe the element
      this.observer.observe(this);

      const link = this.querySelector('[data-js-video-component-link]');
      if ( link ) {
        link.addEventListener('click', e => {
          e.preventDefault();
          this.loadVideoTemplate();
        });
      }

    }

    loadVideoTemplate() {
      
      if ( this.classList.contains('video-opened') ) {
        const media = this.closest('[data-video]');
        if ( media ) this._playMedia(media);
        return;
      }

      const blackout = document.createElement('div');
      blackout.classList.add('video-component__blackout');
      this.append(blackout);
      setTimeout(() => {
        blackout.style.opacity = '1';
      }, 10);

      this.classList.add('video-opened');

      if ( this.querySelector('[data-js-video-component-close]') ) {
        this.querySelector('[data-js-video-component-close]').addEventListener('click', () => {
          this.querySelectorAll('iframe, video').forEach(elm => { elm.remove() });
          blackout.remove();
          this.classList.remove('video-opened');
          this.classList.remove('video-playing');
        });
      }

      setTimeout(() => {
        const template = this.querySelector('template');
        if ( ! template ) return;

        this.querySelector('[data-js-video-component-container]').appendChild(template.content.firstElementChild.cloneNode(true));
        setTimeout(() => {
          this._playMedia(this.closest('[data-video]'));
        }, this.dataset.autoplay === 'true' ? 1 : 500);

        // YouTube/Vimeo: no native play promise — reveal once iframe is in the DOM
        if ( this.dataset.autoplay === 'true' && ! this.querySelector('video') ) {
          this.classList.add('video-playing');
        }

      }, 20);

    }

    onEnterViewport() {

      // Reduced motion: keep the poster, never autoplay the stream
      if ( this._prefersReducedMotion && this.dataset.autoplay === 'true' ) {
        return;
      }

      // Check if data-autoplay is true and if the popup is closed
      if ( this.dataset.autoplay === 'true' && !this.classList.contains('video-opened') ) {
        // Defer stream start so the poster can paint first (better LCP)
        if ( ! this._autoplayArmed ) {
          this._autoplayArmed = true;
          const start = () => {
            if ( this.querySelector('[data-js-video-component-link]') ) {
              this.loadVideoTemplate();
            }
          };
          if ( 'requestIdleCallback' in window ) {
            requestIdleCallback(start, { timeout: 1200 });
          } else {
            setTimeout(start, 400);
          }
        }
      }

      // play video if in viewport
      const media = this.closest('[data-video]');
      if ( media ) {
        this._playMedia(media);
      }

    }

    onExitViewport() {
      // pause video if outside the viewport
      const media = this.closest('[data-video]');
      if ( media ) {
        this._pauseMedia(media);
      }
    }

    _markPlaying() {
      this.classList.add('video-playing');
    }

    _playMedia(media) {
      // triggers the PLAY function of each media type depending on it's source
      switch ( media.dataset.mediaType ) {
        case 'video':
          if ( media.querySelector('video') ) {
            const video = media.querySelector('video');
            // iOS requires muted + playsInline before play(); opacity:0 can also block decode
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.setAttribute('webkit-playsinline', '');

            const onPlaying = () => this._markPlaying();
            video.addEventListener('playing', onPlaying, { once: true });

            const playPromise = video.play();
            if ( playPromise && typeof playPromise.then === 'function' ) {
              playPromise.then(() => {
                this._markPlaying();
              }).catch(() => {
                // Autoplay blocked / not ready — keep poster visible
                video.removeEventListener('playing', onPlaying);
              });
            }
          }
          break;
        case 'youtube':
          if ( media.querySelector('.js-youtube') ) {
            media.querySelector('.js-youtube').contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          }
          break;
        case 'vimeo':
          if ( media.querySelector('.js-vimeo') ) {
            const leVimeoFrame = media.querySelector('.js-vimeo');
            leVimeoFrame.contentWindow.postMessage('{"method":"play"}', '*');
          }
          break;
      }
    }

    _pauseMedia(media) {
      // triggers the PAUSE function of each media type depending on it's source
      switch ( media.dataset.mediaType ) {
        case 'video':
          if ( media.querySelector('video') ) {
            media.querySelector('video').pause();
          }
          break;
        case 'youtube':
          if ( media.querySelector('.js-youtube') ) {
            media.querySelector('.js-youtube').contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
          break;
        case 'vimeo':
          if ( media.querySelector('.js-vimeo') ) {
            media.querySelector('.js-vimeo').contentWindow.postMessage('{"method":"pause"}', '*');
          }
          break;
      }
    }

  }

  if ( typeof customElements.get('video-component') == 'undefined' ) {
    customElements.define('video-component', VideoComponent);
  }

}
